import { TrashIcon } from '@components/Icons/TrashIcon';
import { Toast } from '@components/Toast/Toast';
import { askAgentAboutMedia } from '@lib/starzero-agents';
import { pluralize } from '@lib/utils/units';
import { VERBS, verbMenuIcon } from '@lib/verbs';
import CloseIcon from '@mui/icons-material/Close';
import DriveFileMoveOutlined from '@mui/icons-material/DriveFileMoveOutlined';
import Replay from '@mui/icons-material/Replay';
import { BaseSelectionBar } from '@pages/Search/components/MomentGrid/components/MomentSelection/BaseSelectionBar';
import { useLibrary } from '@stores/Library/Library';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { Media } from '@typings/Media.js';
import { observer } from 'mobx-react';
import PQueue from 'p-queue';
import { toast } from 'sonner';
import { useMediaDeleteModal } from '../../context/MediaDeleteModalContext';
import { useFolderDialogs } from '../Folders/FolderDialogs';
import css from './LibraryVideoSelection.module.scss';

export const LibraryVideoSelection = observer(function LibraryVideoSelection() {
  const folder = useLibraryFolder();
  const library = useLibrary();
  const libraryManager = useLibraryManager();
  const videoSelection = folder.videoSelection;
  const selectedCount = videoSelection.selectedCount;
  const { openDeleteModal } = useMediaDeleteModal();
  const folderDialogs = useFolderDialogs();

  const downloadSelection = async () => {
    if (!videoSelection.isSelecting) {
      return;
    }

    toast.custom(
      (t) => (
        <Toast
          id={t}
          header={`Downloading ${pluralize(selectedCount, 'media file')}...`}
          description="Please do not close this tab."
          variant="success"
        />
      ),
      { duration: 3000 },
    );

    try {
      const selectedVideos = folder.files.filter((file) => videoSelection.isSelected(file._id));

      const promises = selectedVideos.map((video, index) => {
        return libraryManager.renderer.createCompilation(
          [
            {
              libraryId: video.libraryId,
              mediaId: video._id,
              startTime: 0,
              // Intentional: raw duration is used as a clip endpoint, not a
              // display value. Images (frameCount=1) get a ~0.03s clip range,
              // which is the correct compilation endpoint for a single frame.
              // Do not gate with isProbablyImage.
              endTime: video.metadata?.video?.duration || 0,
            },
          ],
          `${video.metadata?.name || 'media'}_${index}.mp4`,
        );
      });

      const results = await Promise.allSettled(promises);

      const failedCount = results.filter((result) => result.status === 'rejected').length;
      const successCount = results.length - failedCount;

      // Show individual error messages for each failure
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const error = result.reason;
          toast.custom(
            (t) => (
              <Toast
                id={t}
                header={`Failed to download media ${index + 1}`}
                description={`Error: ${error instanceof Error ? error.message : 'Unknown error'}`}
                variant="error"
              />
            ),
            { duration: 3000 },
          );
        }
      });

      if (successCount > 0) {
        toast.custom(
          (t) => (
            <Toast
              id={t}
              header="Download Complete"
              description={`Successfully downloaded ${pluralize(successCount, 'media file')}`}
              variant="success"
            />
          ),
          { duration: 3000 },
        );
      }
    } catch (error) {
      toast.custom(
        (t) => (
          <Toast
            id={t}
            header="Download Failed"
            description={`Failed to download media: ${error instanceof Error ? error.message : 'Unknown error'}`}
            variant="error"
          />
        ),
        { duration: 3000 },
      );
    }
  };

  if (!videoSelection.isSelecting) {
    return null;
  }

  // Get selected media for the modal
  const selectedMedia = folder.files.filter((file) => videoSelection.isSelected(file._id));

  const selectedCompletedMedia = selectedMedia.filter((media) => media.status === 'completed');
  const selectedErroredMedia = selectedMedia.filter((media) => media.status === 'errored');

  const allSelectedAreCompleted = selectedCompletedMedia.length === selectedMedia.length;
  const hasNonCompletedMedia = !allSelectedAreCompleted;

  const canOpenSelectionInChat = (() => {
    return selectedCompletedMedia.length > 0;
  })();

  const retrySelectedErroredMedia = async () => {
    if (selectedErroredMedia.length === 0 || !folder.library?.libraryId) {
      return;
    }

    // Remove all selected errored media from selection when retry starts
    const erroredMediaIds = selectedErroredMedia.map((media) => media._id);
    videoSelection.deselectItems(erroredMediaIds);

    const queue = new PQueue({ intervalCap: 2, interval: 1000 });
    const updates = await queue.addAll(
      selectedErroredMedia.map(
        (media) => () => folder.retryMedia(folder.library!.libraryId, media._id),
      ),
    );

    // Count successes and errors
    const errors = updates.filter(
      (update): update is { error: true; message: string } => 'error' in update && update.error,
    );
    const successes = updates.filter((update): update is Media => !('error' in update));

    // Get unique error messages
    const uniqueErrorMessages = [...new Set(errors.map((error) => error.message))];

    // Show toast with results
    let variant: 'success' | 'error';
    let header: string;
    let description: string | undefined;

    if (errors.length === 0) {
      variant = 'success';
      header = `Retrying ${pluralize(successes.length, 'media file')}...`;
    } else if (successes.length === 0) {
      variant = 'error';
      header = `Failed to retry ${errors.length === 1 ? '1 media file' : `all ${pluralize(errors.length, 'media file')}`}`;
      description = uniqueErrorMessages.length > 0 ? uniqueErrorMessages.join(', ') : undefined;
    } else {
      variant = 'error';
      header = `Retried ${successes.length} successfully, ${errors.length} failed`;
      description = uniqueErrorMessages.length > 0 ? uniqueErrorMessages.join(', ') : undefined;
    }

    toast.custom(
      (t) =>
        Toast({
          id: t,
          header,
          description,
          variant,
        }),
      {
        duration: 5000,
      },
    );

    // Refresh all retried media
    selectedErroredMedia.forEach((media) => {
      folder.refreshMediaById(media._id);
    });
  };

  return (
    <>
      <BaseSelectionBar
        selectedCountProps={{
          text: () => `${selectedCount} media selected`,
        }}
        selectAllProps={{
          text: 'Select all',
          onClick: () => {
            videoSelection.selectAllVideos();
            trackingStore.mediaSelectionBarSelectAll({ totalAvailable: folder.files.length });
          },
        }}
        deselectAllProps={{
          text: 'Deselect all',
          onClick: () => {
            videoSelection.deselectAll();
            trackingStore.mediaSelectionBarDeselectAll();
          },
        }}
        closeProps={{
          text: 'Close',
          onClick: () => {
            trackingStore.mediaSelectionBarClosed();
            videoSelection.clearSelection();
          },
          children: <CloseIcon fontSize="small" color="inherit" />,
          tooltip: 'Clear selection and stop selecting media',
        }}
        retryProps={
          hasNonCompletedMedia
            ? {
                text: 'Retry',
                onClick: () => {
                  trackingStore.mediaSelectionBarRetry({
                    mediaCount: selectedErroredMedia.length,
                    libraryId: folder.library?.libraryId ?? '',
                  });
                  retrySelectedErroredMedia();
                },
                children: <Replay fontSize="small" color="inherit" />,
                disabled: selectedErroredMedia.length === 0,
                tooltip:
                  selectedErroredMedia.length === 0
                    ? 'No selected errored media to retry'
                    : 'Retry selected errored media',
              }
            : undefined
        }
        askAnAgentProps={{
          // Bulk version of the same verb: same label, same glyph. Only the event differs
          // (`mediaSelectionBarAskAnAgent`), because this one reports a count rather than one item.
          text: VERBS['work-with-agent'].label,
          onClick: () => {
            // `LibraryFolderStore` takes a non-optional `library`, so in practice
            // this is always set — but every other read of it in this file
            // optional-chains, so match that rather than being the one place
            // that can throw. Bailing beats building `?library=undefined`.
            const libraryId = folder.library?.libraryId;
            if (!libraryId) return;
            trackingStore.mediaSelectionBarAskAnAgent({
              mediaCount: selectedCompletedMedia.length,
              libraryId,
            });
            askAgentAboutMedia(
              libraryId,
              selectedCompletedMedia.map((media) => media._id),
              {
                surface: 'library',
                control: 'media-selection-bar',
              },
            );
          },
          children: verbMenuIcon('work-with-agent'),
          disabled: !canOpenSelectionInChat,
          tooltip: !canOpenSelectionInChat
            ? selectedCompletedMedia.length > 0
              ? 'Please wait for the media to be processed before asking an agent'
              : 'No completed media selected'
            : 'Ask an agent about the selected media',
        }}
        moveProps={{
          text: 'Move',
          onClick: () =>
            folderDialogs.moveMedia(
              selectedMedia.map((media) => media._id),
              'bulk',
            ),
          children: <DriveFileMoveOutlined fontSize="small" color="inherit" />,
          disabled: selectedMedia.length === 0 || library.isReadOnly,
          tooltip:
            selectedMedia.length === 0
              ? 'No media selected'
              : library.isReadOnly
                ? 'Moving is not possible because this library is read-only'
                : 'Move the selected media to a folder',
        }}
        deleteProps={{
          text: '',
          onClick: () => {
            trackingStore.mediaSelectionBarDelete({
              mediaCount: selectedMedia.length,
              libraryId: folder.library?.libraryId ?? '',
            });
            openDeleteModal(selectedMedia, () => videoSelection.clearSelection());
          },
          disabled: selectedMedia.length === 0 || library.isReadOnly,
          tooltip:
            selectedMedia.length === 0
              ? 'No media selected'
              : library.isReadOnly
                ? 'Deleting is not possible because this library is read-only'
                : 'Permanently delete the selected media.',
          children: (
            <span className={css.trashIconWrapper}>
              <TrashIcon
                disabled={
                  libraryManager.renderer.isRendering ||
                  videoSelection.selectedCount === 0 ||
                  library.isReadOnly
                }
                width={20}
                height={20}
              />
            </span>
          ),
        }}
      />
    </>
  );
});
